import { useState } from 'react';
import { api, ApiError } from '../api/client';
import { useTenant } from './Shell';
import { Button, Dialog, Notice } from './ui';

/** Voicemail transcripts for one mailbox, as the person looking sees them. */
export interface TranscriptState {
  /** The company has transcripts on. */
  company: boolean;
  /** This mailbox has them on (only ever with the company on). */
  on: boolean;
  /** The person looking may switch it (admins always; people their own when the company lets them). */
  canChange: boolean;
  /** The person looking may turn transcripts on for the company first (its admins). */
  canTurnOnCompany: boolean;
}

interface Target {
  /** The mailboxes (extensions) to switch. */
  mailboxes: string[];
  /** The person's own mailbox (switched through their own route). */
  own: boolean;
  /** Whose voicemail, for the dialog: "Dana Ruiz’s voicemail", "your voicemail". */
  label: string;
}

const messageOf = (e: unknown) => (e instanceof Error ? e.message : 'That didn’t save.');

/**
 * Switching voicemail transcripts on or off. When the company has them off, its admins are asked
 * to turn them on for the company first: one dialog, then the company and the mailbox in one go.
 * Render `dialog` once wherever this is used.
 */
export function useTranscriptSwitch(domain: string, onChanged: () => void) {
  const { tenant } = useTenant();
  const [asking, setAsking] = useState<Target | null>(null);
  const [selfService, setSelfService] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const apply = (t: Target, on: boolean) => (t.own ? api.setMyTranscripts(domain, on) : api.setTranscriptBoxes(domain, t.mailboxes, on));

  function ask(t: Target) {
    setSelfService(false);
    setError('');
    setAsking(t);
  }

  async function change(t: Target, on: boolean, state: Pick<TranscriptState, 'company' | 'canTurnOnCompany'>) {
    setError('');
    if (on && !state.company) {
      if (state.canTurnOnCompany) ask(t);
      else setError('Voicemail transcripts are off for your company. Ask your company’s admins.');
      return;
    }
    setBusy(t.mailboxes.join(','));
    try {
      await apply(t, on);
      onChanged();
    } catch (e) {
      // Switched off for the company in the meantime: ask to turn it on again.
      if (on && e instanceof ApiError && e.code === 'TRANSCRIPTS_OFF' && state.canTurnOnCompany) ask(t);
      else setError(messageOf(e));
    } finally {
      setBusy('');
    }
  }

  async function confirm() {
    if (!asking) return;
    setBusy('company');
    setError('');
    try {
      // On for this mailbox only, as the dialog says (not every mailbox: that's chosen in Settings).
      await api.saveCompanySettings(domain, { transcripts: true, transcriptsEvery: false, ...(selfService ? { transcriptsSelf: true } : {}) });
      await apply(asking, true);
      setAsking(null);
      onChanged();
    } catch (e) {
      setError(messageOf(e));
      // The company may be on even if the mailbox didn't save: show what's there now.
      onChanged();
    } finally {
      setBusy('');
    }
  }

  const close = () => {
    if (busy) return;
    setAsking(null);
    setError('');
  };

  const dialog = (
    <Dialog
      open={Boolean(asking)}
      onClose={close}
      title="Turn on voicemail transcripts?"
      subtitle={tenant.name}
      foot={
        <>
          <Button variant="ghost" onClick={close} disabled={Boolean(busy)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void confirm()} disabled={Boolean(busy)}>
            {busy ? 'Turning on…' : 'Turn on'}
          </Button>
        </>
      }
    >
      <div className="section-stack">
        <p>
          Voicemail transcripts are off for {tenant.name}. Turn them on for the company, and Stitch starts writing out {asking?.label ?? 'this voicemail'}, so it can be read
          without playing it.
        </p>
        <p className="muted">
          Anyone else’s voicemail is written out only once it’s turned on for them: on their page, on Voicemail boxes, or in Settings (where you can also choose every
          mailbox). The audio goes only to Stitch’s own transcription service.
        </p>
        <label className="check-row">
          <input type="checkbox" checked={selfService} disabled={Boolean(busy)} onChange={(e) => setSelfService(e.target.checked)} />
          <span className="cell-stack">
            <span>Let people turn it on or off for their own voicemail</span>
            <small className="muted">On My phone. You can change this later in Settings.</small>
          </span>
        </label>
        {error && <Notice tone="crit">{error}</Notice>}
      </div>
    </Dialog>
  );

  return { change, busy, error: asking ? '' : error, dialog };
}

/**
 * The "Write out voicemail" switch for one mailbox, with its dialog. Admins see it even when the
 * company has transcripts off (it asks to turn them on); people see theirs only when they may
 * change it, and otherwise just whether their voicemail is written out.
 */
export function TranscriptSwitch({
  domain,
  mailbox,
  own,
  whose,
  state,
  onChanged
}: {
  domain: string;
  mailbox: string;
  own: boolean;
  /** "Dana Ruiz’s", "this box’s" (own mailboxes say "my"). */
  whose: string;
  state: TranscriptState;
  onChanged: () => void;
}) {
  const sw = useTranscriptSwitch(domain, onChanged);
  const on = state.company && state.on;
  if (!state.canChange) {
    if (!on) return null;
    return (
      <p className="vm-transcripts muted">
        {own ? 'Your voicemail is written out, so you can read it without playing it. Your company’s admins look after this.' : `${whose} voicemail is written out.`}
      </p>
    );
  }
  const target: Target = { mailboxes: [mailbox], own, label: own ? 'your voicemail' : `${whose} voicemail` };
  return (
    <>
      <label className="check-row vm-transcripts">
        <input type="checkbox" checked={on} disabled={Boolean(sw.busy)} onChange={(e) => void sw.change(target, e.target.checked, state)} />
        <span className="cell-stack">
          <span>Write out {own ? 'my' : whose} voicemail</span>
          <small className="muted">
            {on
              ? 'New messages are written out, so they can be read without playing them.'
              : state.company
                ? 'Off: messages are kept as audio only. Turning it off deletes the transcripts.'
                : 'Off for the company: turning it on here turns it on for the company first.'}
          </small>
        </span>
      </label>
      {sw.error && <Notice tone="crit">{sw.error}</Notice>}
      {sw.dialog}
    </>
  );
}
